import type { Debt } from '@familywallet/shared';

/**
 * A debt between two members is two rows (one per side). When the viewer sees both, lists show
 * it once, from the lender's side.
 */
export function withoutDuplicatePairs(debts: readonly Debt[]): Debt[] {
  const ids = new Set(debts.map((d) => d.id));
  return debts.filter(
    (d) => !(d.direction === 'borrowed' && d.pairDebtId !== null && ids.has(d.pairDebtId)),
  );
}

/** A YYYY-MM-DD date plus `days` (calendar arithmetic at noon UTC, so no day shifts). */
export function addDays(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** Debts with an open installment, overdue first, then by due date. */
export function upcomingInstallments(debts: readonly Debt[]): Debt[] {
  return withoutDuplicatePairs(debts)
    .filter((d) => d.nextInstallment !== null)
    .sort((a, b) => {
      const x = a.nextInstallment!;
      const y = b.nextInstallment!;
      const overdue = (y.state === 'overdue' ? 1 : 0) - (x.state === 'overdue' ? 1 : 0);
      return overdue || x.dueOn.localeCompare(y.dueOn) || a.id.localeCompare(b.id);
    });
}

/** "Due soon" on Home: overdue installments and those due within the next 7 days. */
export function dueSoon(debts: readonly Debt[], today: string): Debt[] {
  const limit = addDays(today, 7);
  return upcomingInstallments(debts).filter((d) => d.nextInstallment!.dueOn <= limit);
}

/** Overdue installments across the debts (each debt between members counted once). */
export function overdueCount(debts: readonly Debt[]): number {
  return withoutDuplicatePairs(debts).reduce((sum, d) => sum + d.overdueInstallments, 0);
}

/** Credit items with a due date, not settled, overdue or due within the next 7 days. */
export function creditDue(debts: readonly Debt[], today: string): Debt[] {
  const limit = addDays(today, 7);
  return debts
    .filter((d) => d.credit?.dueState && d.credit.dueOn !== null && d.credit.dueOn <= limit)
    .sort((a, b) => a.credit!.dueOn!.localeCompare(b.credit!.dueOn!) || a.id.localeCompare(b.id));
}
